import React, { useState, useRef } from 'react';
import { MenuItem, Popover } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import BaseButton from '../form/BaseButton';

export type PopoverContentType = {
  className?: string,
  disabled?: boolean,
  isActive?: boolean,
  label: React.ReactElement | string,
  onClick?: () => void,
  onMouseEnter?: () => void,
  prefix?: React.ReactElement | string,
  value: string,
}

type PropsIn = {
  active?: boolean,
  badge?: number,
  buttonColor?: string,
  buttonId:string,
  buttonSize?: 'sm' | 'md' | 'lg',
  className: string,
  collapseOnMobile?: boolean,
  collapseOnSmallDesktop?: boolean,
  disabled?: boolean,
  disabledExportMenuItems?: object,
  hoverOnly?: boolean
  icon?: React.ReactElement,
  label?: string,
  onClick?: () => void,
  popoverContent?: PopoverContentType[],
  popoverTitle?: string,
  position?: 'left' | 'center' | 'right',
  topPosition?: boolean,
  tooltip?: string,
}

export default function HeaderPopover(props: PropsIn) {
  const {
    buttonId, badge, collapseOnMobile, collapseOnSmallDesktop, disabled, icon, active,
    label, popoverContent, position, tooltip, hoverOnly, className, popoverTitle,
    disabledExportMenuItems, buttonSize, onClick, buttonColor, topPosition
  } = props;

  const [anchor, setAnchor] = useState<HTMLButtonElement | HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchor);
  const id = open ? 'popover' : undefined;

  const handleClose = () => {
    setAnchor(null);
  };

  const handlePopoverMouseLeave = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // check if mouse is leaving both popover and its content
    if (!popoverRef.current?.contains(event.relatedTarget as Node)) {
      handleClose();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
    setAnchor(event.currentTarget);
    if (hoverOnly) {
      setTimeout(() => {
        handleClose();
      }, 3000);
    }
    if (onClick) {
      onClick();
    }
  };

  const positionProps = {
    anchorOrigin: {
      vertical: topPosition ? 'top' : 40,
      horizontal: topPosition ? 'center' : position || 'left',
    },
    transformOrigin: {
      vertical: topPosition ? 'bottom' : 'top',
      horizontal: topPosition ? 'center' : position || 'left',
    },
  };

  return (
    <div
      className={className || ''}
      onMouseEnter={hoverOnly ? handleClick : undefined}
      onMouseLeave={hoverOnly ? handleClose : undefined}
      ref={containerRef}
    >
      <BaseButton
        id={buttonId || ''}
        tooltipText={tooltip || ''}
        onClick={!hoverOnly ? handleClick : undefined}
        collapseOnMobile={collapseOnMobile}
        collapseOnSmallDesktop={collapseOnSmallDesktop}
        className="force-large"
        startIcon={label && icon}
        disabled={disabled}
        isIconButton={!label}
        active={active}
        badge={badge}
        buttonSize={buttonSize}
        color={buttonColor}
      >
        {label || icon}
      </BaseButton>
      {popoverContent && (
        /* A `Menu` that is displayed on top of the page. */
        <Popover
          id={id}
          className={className}
          open={open}
          anchorEl={anchor}
          onClose={handleClose}
          container={containerRef.current}
          {...positionProps}
        >
          <div ref={popoverRef} onMouseLeave={hoverOnly ? handlePopoverMouseLeave : undefined}>
            {popoverTitle && <h3 className="title">{popoverTitle}</h3>}
            {popoverContent.map((element, i) => {
              const isMenuItemDisabled = disabledExportMenuItems?.hasOwnProperty(element.label);
              return (
                <MenuItem
                  key={`${element.value}-${i}`}
                  value={element.value}
                  className={`${element.className} ${isMenuItemDisabled || element.disabled ? 'disabled-popover-menu-items' : ''}`}
                  onClick={() => {
                    if (!isMenuItemDisabled && !element.disabled) {
                      handleClose();
                      element.onClick();
                    }
                  }}
                  onMouseEnter={() => {
                    if (!isMenuItemDisabled && !element.disabled && element.onMouseEnter) {
                      element.onMouseEnter();
                    }
                  }}
                >
                  <Tooltip arrow placement="right" title={isMenuItemDisabled ? disabledExportMenuItems[element.label] : ''}>
                    <div>
                      {element.prefix}
                      {element.label}
                      {element.isActive && <span className="menu-item-checkmark">✓</span>}
                    </div>
                  </Tooltip>
                </MenuItem>
              );
            })}
          </div>
        </Popover>
      )}
    </div>
  );
}
